import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  RefreshCw, Search, ChevronLeft, ExternalLink, AlertTriangle, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { adminDossiersList } from "../../lib/api";
import { DossierVisibilityControls } from "./DossierVisibilityControls";

/**
 * AdminDossierPullback — unified pullback / unpublish console.
 *
 * Lists every dossier in the system (topics + ef_entities) with its current
 * status and pullback timestamps. Selecting a row opens the per-dossier
 * visibility controls (with confirmation modals, reason capture, and the
 * route diagnostic).
 */

const STATUS_META = {
  draft:     { label: "DRAFT",     color: "#A39E93" },
  review:    { label: "REVIEW",    color: "#E0A526" },
  published: { label: "PUBLISHED", color: "#5DD4B3" },
  archived:  { label: "ARCHIVED",  color: "#D92D20" },
};

const STATUS_TABS = ["all", "published", "review", "draft", "archived"];
const TYPE_FILTERS = [
  { key: "all", label: "All Dossiers" },
  { key: "topic", label: "Topics" },
  { key: "ef_entity", label: "Epstein Files" },
];

export const AdminDossierPullback = ({ token, userToken = null }) => {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [statusTab, setStatusTab] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null); // a row from `rows`

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusTab !== "all") params.status = statusTab;
      if (typeFilter !== "all") params.dossier_type = typeFilter;
      const r = await adminDossiersList(token, params, userToken);
      setRows(r.rows || []);
      setCounts(r.counts || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load dossiers");
    } finally {
      setLoading(false);
    }
  }, [token, userToken, statusTab, typeFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.title || "").toLowerCase().includes(q)
      || (r.slug || "").toLowerCase().includes(q)
      || (r.category || "").toLowerCase().includes(q),
    );
  }, [rows, query]);

  const onChanged = useCallback((res) => {
    // Refresh the row in-place, also reload counts.
    if (!res?.dossier_id) return;
    setRows((prev) => prev.map((r) =>
      r.dossier_id === res.dossier_id && r.dossier_type === res.dossier_type
        ? { ...r, status: res.new_status }
        : r
    ));
    setSelected((s) => (s && s.dossier_id === res.dossier_id
      ? { ...s, status: res.new_status }
      : s));
    load();
  }, [load]);

  if (selected) {
    return (
      <DossierDetail
        token={token}
        userToken={userToken}
        row={selected}
        onBack={() => setSelected(null)}
        onChanged={onChanged}
      />
    );
  }

  return (
    <div data-testid="admin-dossier-pullback">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-2 flex items-center gap-2">
            <AlertTriangle size={13} /> // DOSSIER PULLBACK CONSOLE
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-[#E4DFD3]">
            Pullback & Unpublish
          </h1>
          <p className="text-[#A39E93] mt-1 text-sm max-w-2xl">
            Unified visibility control across every dossier in The Archive — topics and
            Epstein Files entities. Pull from public, move to draft, or archive without
            losing any data. Every action is audit-logged.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="dpc-refresh"
          className="flex items-center gap-2 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]"
        >
          <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Type filter */}
      <div className="flex flex-wrap gap-1 mb-2" data-testid="dpc-type-filter">
        {TYPE_FILTERS.map((t) => {
          const active = typeFilter === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTypeFilter(t.key)}
              data-testid={`dpc-type-${t.key}`}
              className={
                "px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] border " +
                (active
                  ? "border-[#D92D20] text-[#D92D20] bg-[#1a0a0a]"
                  : "border-[#454038] text-[#A39E93] hover:border-[#D92D20]")
              }
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Status tabs */}
      <div className="flex overflow-x-auto whitespace-nowrap gap-1 mb-3 border-b border-[#2D2A26]">
        {STATUS_TABS.map((s) => {
          const active = statusTab === s;
          const count = s === "all"
            ? Object.values(counts).reduce((a, b) => a + b, 0)
            : (counts[s] || 0);
          const color = s === "all" ? "#D92D20" : (STATUS_META[s]?.color || "#A39E93");
          return (
            <button
              key={s}
              onClick={() => setStatusTab(s)}
              data-testid={`dpc-status-${s}`}
              style={active ? { color, borderColor: color } : undefined}
              className={
                "px-3 py-2 -mb-px font-mono text-[10px] uppercase tracking-[0.25em] border-b-2 shrink-0 " +
                (active ? "" : "border-transparent text-[#A39E93] hover:text-[#E4DFD3]")
              }
            >
              {s} <span className="ml-1 text-[#757065]">·&nbsp;{count}</span>
            </button>
          );
        })}
      </div>

      <div className="relative mb-3">
        <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#5a544a]" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search title, slug, category…"
          data-testid="dpc-search"
          className="w-full bg-[#0a0907] border border-[#2D2A26] focus:border-[#D92D20] outline-none pl-7 pr-3 py-2 font-mono text-[12px] text-[#E4DFD3] placeholder:text-[#5a544a]"
        />
      </div>

      {loading && rows.length === 0 ? (
        <div className="font-mono text-xs text-[#757065]">// LOADING…</div>
      ) : (
        <div className="border border-[#2D2A26] bg-[#131313]" data-testid="dpc-list">
          {filtered.map((r) => {
            const sm = STATUS_META[r.status] || STATUS_META.draft;
            return (
              <button
                key={`${r.dossier_type}-${r.dossier_id}`}
                onClick={() => setSelected(r)}
                data-testid={`dpc-row-${r.dossier_type}-${r.dossier_id}`}
                className="flex flex-wrap items-center gap-3 w-full text-left border-b border-[#2D2A26] px-3 py-2 hover:bg-[#0a0907]"
              >
                <span
                  style={{ color: sm.color, borderColor: sm.color }}
                  className="font-mono text-[9px] uppercase tracking-[0.3em] border px-2 py-0.5 shrink-0"
                >
                  {sm.label}
                </span>
                <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#9FA8D4] border border-[#9FA8D4]/40 px-2 py-0.5 shrink-0">
                  {r.dossier_type === "topic" ? "TOPIC" : "EF · ENTITY"}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-[#E4DFD3] truncate">{r.title}</div>
                  <div className="font-mono text-[10px] text-[#757065] truncate">
                    {r.public_url}
                    {r.category ? ` · ${r.category}` : ""}
                  </div>
                </div>
                <span className="font-mono text-[9px] text-[#D92D20] uppercase tracking-[0.25em] shrink-0">
                  Manage →
                </span>
              </button>
            );
          })}
          {filtered.length === 0 && (
            <div className="px-3 py-10 text-center text-[#757065] font-mono text-xs uppercase tracking-widest">
              No dossiers match.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const DossierDetail = ({ token, userToken, row, onBack, onChanged }) => {
  return (
    <div data-testid="dpc-detail">
      <div className="flex items-center gap-2 mb-3">
        <button
          onClick={onBack}
          data-testid="dpc-detail-back"
          className="flex items-center gap-1.5 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em]"
        >
          <ChevronLeft size={12} /> Back
        </button>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] truncate">
          // {row.dossier_type === "topic" ? "TOPIC" : "EF · ENTITY"} · {row.slug}
        </div>
      </div>

      <div className="border border-[#2D2A26] bg-[#0F0F0F] p-4 mb-3">
        <div className="font-display text-xl text-[#E4DFD3]">{row.title}</div>
        <div className="font-mono text-[10px] text-[#757065] mt-1">
          {row.category} · {row.dossier_id}
        </div>
        {row.public_url && (
          <Link
            to={row.public_url}
            target="_blank"
            className="inline-flex items-center gap-1.5 mt-2 font-mono text-[10px] text-[#A39E93] hover:text-[#D92D20]"
          >
            <ExternalLink size={11} /> {row.public_url}
          </Link>
        )}
      </div>

      <DossierVisibilityControls
        dossierType={row.dossier_type}
        dossierId={row.dossier_id}
        slug={row.slug}
        title={row.title}
        currentStatus={row.status}
        token={token}
        userToken={userToken}
        onChanged={onChanged}
      />
    </div>
  );
};

export default AdminDossierPullback;
